import { variantId, variantPrice } from "../utils/exchangePolicy.js";
import {
  catalogProductSummary,
  findVariantByOptions,
  formatBRL,
  formatPriceRange,
  optionValueState,
  productOptions,
  stockAvailabilityLabel,
  variantOptionMap,
} from "../utils/productSearch.js";
import styles from "./VariantPicker.module.css";

const VariantPicker = ({
  product,
  item,
  options,
  selectedOptions,
  onOptionsChange,
  selectedVariantId,
  onSelect,
  onChangeProduct,
  policy,
}) => {
  const groups = productOptions(product);
  const names = groups.map((group) => group.name);
  const summary = catalogProductSummary(product, item, policy);
  const selected = options.find((option) => String(variantId(option.variant)) === String(selectedVariantId));
  const selectedVariant = selected?.variant;
  const selectedAvailability = stockAvailabilityLabel(selectedVariant);

  const handleOption = (name, value) => {
    const next = { ...selectedOptions, [name]: value };
    const match = findVariantByOptions(product?.variants || [], next, names);
    if (match) {
      const annotated = options.find((option) => String(variantId(option.variant)) === String(variantId(match)));
      if (annotated?.allowed) {
        onOptionsChange({ ...next, ...variantOptionMap(match, names) });
        onSelect(variantId(match));
        return;
      }
    }
    onOptionsChange(next);
    onSelect("");
  };

  return (
    <div className={styles.stack}>
      <div className={styles.summary}>
        {product?.image ? <img src={product.image} alt="" /> : <div className={styles.fallback} aria-hidden="true" />}
        <div className={styles.summaryBody}>
          <p className={styles.summaryTitle}>{product?.title || item?.title || item?.name}</p>
          <p className={styles.summaryMeta}>
            {(Number(summary.maxPrice) > 0 && formatPriceRange(summary.minPrice, summary.maxPrice)) || `R$ ${formatBRL(item?.price)}`}
          </p>
          {onChangeProduct ? (
            <button type="button" className={styles.changeProduct} onClick={onChangeProduct}>
              Trocar produto
            </button>
          ) : null}
        </div>
      </div>

      {groups.length > 0 ? (
        <>
        <p className={styles.heading}>Escolha tamanho e cor</p>
        {groups.map((group) => (
          <div key={group.name} className={styles.group}>
            <p className={styles.groupLabel}>{group.name}</p>
            <div className={styles.chips} role="radiogroup" aria-label={group.name}>
              {group.values.map((value) => {
                const state = optionValueState(product.variants || [], group.name, value, selectedOptions, names);
                const checked = selectedOptions[group.name] === value;
                const disabled = state !== "available";
                return (
                  <button
                    key={`${group.name}-${value}`}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    disabled={disabled}
                    className={`${styles.chip} ${checked ? styles.chipChecked : ""} ${state === "oos" ? styles.chipOos : ""}`}
                    onClick={() => handleOption(group.name, value)}
                  >
                    <span>{value}</span>
                    {state === "oos" ? <span className={styles.chipHint}>Esgotado</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        </>
      ) : (
        options.length === 1 && /^default title$/i.test(options[0]?.variant?.title || "") ? (
          <p className={styles.heading}>Este produto não tem outros tamanhos ou cores. Use "Outro produto" para escolher outro item.</p>
        ) : (
        <div role="radiogroup" aria-label={`Variações de ${product?.title || item?.title || "produto"}`}>
          <p className={styles.heading}>Escolha a variação</p>
          {options.map(({ variant, allowed, reason, dueAmount, inStock }) => {
            const id = variantId(variant);
            const availability = stockAvailabilityLabel(variant);
            const disabled = !allowed;
            return (
              <label
                key={id}
                className={`${styles.variant} ${String(selectedVariantId) === String(id) ? styles.checked : ""} ${disabled ? styles.disabled : ""}`}
              >
                <input
                  type="radio"
                  name={`variant-${item.sku_id}-${product?.product_id || product?.id || "local"}`}
                  value={id}
                  checked={String(selectedVariantId) === String(id)}
                  disabled={disabled}
                  onChange={() => onSelect(id)}
                />
                <span>
                  <strong>{variant.title || "Variação"}</strong>
                  <span className={styles.meta}>
                    R$ {formatBRL(variantPrice(variant, item.price))}
                    {availability ? ` · ${availability}` : ""}
                    {dueAmount > 0 && allowed ? ` · diferença a pagar R$ ${formatBRL(dueAmount)}` : ""}
                    {disabled ? ` · ${reason || (inStock ? "Indisponível" : "Esgotado")}` : ""}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        )
      )}

      {selectedVariant ? (
        <div className={styles.confirm} role="status">
          <p className={styles.confirmLabel}>Variação escolhida</p>
          <p className={styles.confirmTitle}>{selectedVariant.title || "Variação"}</p>
          <p className={styles.meta}>
            {selectedVariant.sku ? `SKU ${selectedVariant.sku} · ` : ""}
            R$ {formatBRL(variantPrice(selectedVariant, item.price))}
            {selectedAvailability ? ` · ${selectedAvailability}` : ""}
            {selected?.dueAmount > 0 ? ` · diferença a pagar R$ ${formatBRL(selected.dueAmount)}` : ""}
          </p>
        </div>
      ) : (
        <p className={styles.pending}>Escolha tamanho e cor para confirmar a variação.</p>
      )}
    </div>
  );
};

export default VariantPicker;
